<script setup lang="ts">
import { onUnmounted, ref } from 'vue';

const props = defineProps<{
    beforeSrc: string;
    afterSrc: string;
}>();

const containerRef = ref<HTMLElement | null>(null);
const position = ref(50);
const dragging = ref(false);

function updatePosition(clientX: number): void {
    const el = containerRef.value;

    if (!el) {
        return;
    }

    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    position.value = Math.max(0, Math.min(100, pct));
}

function onPointerDown(e: PointerEvent): void {
    dragging.value = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    updatePosition(e.clientX);
}

function onPointerMove(e: PointerEvent): void {
    if (!dragging.value) {
        return;
    }

    updatePosition(e.clientX);
}

function onPointerUp(): void {
    dragging.value = false;
}

onUnmounted(() => {
    dragging.value = false;
});
</script>

<template>
    <div
        ref="containerRef"
        class="relative overflow-hidden rounded-lg select-none"
        style="touch-action: none"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
    >
        <!-- Before image (full width, bottom layer) -->
        <img
            :src="props.beforeSrc"
            class="block h-auto w-full"
            alt="Before"
            draggable="false"
        />

        <!-- After image (clipped, top layer) -->
        <img
            :src="props.afterSrc"
            class="absolute inset-0 h-auto w-full"
            :style="{ clipPath: `inset(0 ${100 - position}% 0 0)` }"
            alt="After"
            draggable="false"
        />

        <!-- Divider handle -->
        <div
            class="absolute top-0 bottom-0 z-10 flex w-1 -translate-x-1/2 cursor-ew-resize flex-col items-center"
            :style="{ left: `${position}%` }"
        >
            <div class="h-full w-0.5 bg-white/80 shadow" />
            <div
                class="absolute top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg"
                :style="{ left: '50%' }"
            >
                <svg
                    viewBox="0 0 20 20"
                    class="h-4 w-4 text-gray-600"
                    fill="currentColor"
                >
                    <path
                        d="M7 4l-4 6 4 6M13 4l4 6-4 6"
                        stroke="currentColor"
                        stroke-width="2"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </div>
        </div>

        <!-- Labels -->
        <span
            class="pointer-events-none absolute top-2 left-2 rounded bg-black/50 px-1.5 py-0.5 text-xs text-white"
        >
            Before
        </span>
        <span
            class="pointer-events-none absolute top-2 right-2 rounded bg-black/50 px-1.5 py-0.5 text-xs text-white"
        >
            After
        </span>
    </div>
</template>
